import { CheckoutContext } from '@/context/CheckoutContext';
import { useCallback, useContext, useState } from 'react';
import { ProviderEnum } from '@/types/common';
import type { SavedCard } from '@/types/checkout';
import { getMaskedSavedPaypalLabel } from '@/utils/paypal';
import { useTranslate } from '@/hooks/useTranslate';
import { cn } from '@/utils/cn';
import { useRemoveCard } from '@/api/card';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import { Popover, PopoverItem } from '../Common/Popover';
import { PayButton } from '../PaymentInputs/PayButton';
import { useIsBottomSheet } from '@/context/BottomSheetContext';
import PaypalLogo from '@/assets/icons/payments/paypal.svg';

interface SavedPaypalFormProps {
  account: SavedCard;
  // Switch this checkout to the full PayPal SDK flow so the buyer can
  // authenticate with a different PayPal account.
  onUseDifferentAccount: () => void;
}

/**
 * One-click PayPal form for a returning buyer who has a vaulted PayPal account.
 * Charges the saved token server-side (no PayPal re-auth / redirect) — the
 * account is NOT rendered in the saved-cards list (see checkout-gateway#750).
 *
 * Mirrors the saved-CARD layout: an informational row (PayPal logo + masked
 * account email + a 3-dot menu to switch accounts or delete the vaulted token)
 * followed by the standard blue Pay CTA. Used for SINGLE / last-payment /
 * PayPal-as-APM — the behaviour is identical to before, only the UI changed
 * (the row is no longer the pay trigger; the Pay button is).
 */
export const SavedPaypalForm = ({ account, onUseDifferentAccount }: SavedPaypalFormProps) => {
  const {
    actions: { runPayment },
    payment: { isFormValid, isInPaymentProcess, isExpressPaymentInProcess },
    card: { clearSavedPaypalAccount },
  } = useContext(CheckoutContext);
  const { t } = useTranslate();
  const { mutate: removeCardMutation } = useRemoveCard();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // The swipe-up sheet shows the vaulted PayPal account as a clean, non-managed
  // row — no account "⋮" menu (Figma). Switching / removing the account stays on
  // the DEFAULT flow reached via "Use Other Payment Method".
  const isBottomSheet = useIsBottomSheet();

  const isPaymentInFlight = isInPaymentProcess || isExpressPaymentInProcess;
  const isDisabled = !isFormValid || isPaymentInFlight;

  const handlePay = useCallback(() => {
    if (isDisabled) return;
    runPayment({ savedToken: account.pspCardIdentifier });
  }, [account.pspCardIdentifier, isDisabled, runPayment]);

  const handleUseDifferent = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsMenuOpen(false);
      onUseDifferentAccount();
    },
    [onUseDifferentAccount]
  );

  const handleDelete = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsMenuOpen(false);
      // Revoke the vaulted token server-side (Braintree paymentMethod.delete),
      // then drop it from local state so the SDK re-auth flow takes over.
      if (account.pspCardIdentifier) {
        removeCardMutation({
          pspCardIdentifier: account.pspCardIdentifier,
          paymentProvider: (account.paymentProvider as ProviderEnum) ?? ProviderEnum.PAYPAL,
        });
      }
      clearSavedPaypalAccount();
    },
    [account.pspCardIdentifier, account.paymentProvider, removeCardMutation, clearSavedPaypalAccount]
  );

  return (
    <div data-testid="saved-paypal-form" className="flex flex-col gap-3">
      <InputTaxComponent />
      <div
        data-testid="saved-paypal-row"
        className={cn(
          'flex h-14 w-full items-center justify-between rounded-lg border border-base-border px-3',
          // Only the Pay button reflects form/ZIP validity. The tile must stay
          // visually normal AND interactive on an invalid/empty ZIP so the user
          // can still open the account menu (change / delete) — matching the
          // saved-CARD row. Dimming it here also created an `opacity` stacking
          // context that trapped the account-menu dropdown BEHIND the Pay
          // button. We only dim while a payment is actually in flight.
          isPaymentInFlight && 'opacity-60'
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <img src={PaypalLogo} alt="PayPal" className="h-4 shrink-0" />
          <span
            data-testid="saved-paypal-label"
            data-mp-mask
            className="mp-mask truncate text-xs color-base-foreground"
          >
            {getMaskedSavedPaypalLabel(account)}
          </span>
        </div>
        {!isBottomSheet && (
          <div className="shrink-0" onClick={(event) => event.stopPropagation()}>
            <Popover
              isOpen={isMenuOpen}
              onOpenChange={setIsMenuOpen}
              // Matches the design-system "DropdownMenu / Menu" (shadcn): 6px
              // radius, base border, shadow/md and 4px inner padding so the items
              // sit inset with their own rounded hover surface.
              contentClassName="min-w-[8rem] rounded-md border-[#e4e4e7] p-1 shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.06),0px_4px_6px_-1px_rgba(0,0,0,0.1)]"
              trigger={
                <div
                  data-testid="saved-paypal-menu-trigger"
                  className="flex min-w-4 min-h-4 items-center justify-center p-2"
                >
                  <FontAwesomeIcon icon="fa-solid fa-ellipsis-vertical" className="text-xs! cursor-pointer" />
                </div>
              }
            >
              <PopoverItem
                data-testid="saved-paypal-use-different"
                onClick={handleUseDifferent}
                className="rounded-sm px-2 py-1.5 font-normal hover:bg-base-accent hover:text-base-primary"
              >
                <span className="whitespace-nowrap">{t('change')}</span>
              </PopoverItem>
              <PopoverItem
                data-testid="saved-paypal-delete"
                onClick={handleDelete}
                className="rounded-sm px-2 py-1.5 font-normal hover:bg-base-accent hover:text-base-primary"
              >
                <span className="whitespace-nowrap">{t('delete')}</span>
              </PopoverItem>
            </Popover>
          </div>
        )}
      </div>
      <PayButton onClick={handlePay} isDisabled={isDisabled} />
    </div>
  );
};
